import * as React from 'react';

import { type DialogProps } from '@radix-ui/react-dialog';
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';

import { cn } from '../lib/utils';

import { Dialog, DialogContent } from './dialog';

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'vai-flex vai-h-full vai-w-full vai-flex-col vai-overflow-hidden vai-rounded-md vai-bg-popover vai-text-popover-foreground',
      className,
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="vai-overflow-hidden vai-p-0 vai-shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:vai-px-2 [&_[cmdk-group-heading]]:vai-font-medium [&_[cmdk-group-heading]]:vai-text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:vai-pt-0 [&_[cmdk-group]]:vai-px-2 [&_[cmdk-input-wrapper]_svg]:vai-h-5 [&_[cmdk-input-wrapper]_svg]:vai-w-5 [&_[cmdk-input]]:vai-h-12 [&_[cmdk-item]]:vai-px-2 [&_[cmdk-item]]:vai-py-3 [&_[cmdk-item]_svg]:vai-h-5 [&_[cmdk-item]_svg]:vai-w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    className="vai-flex vai-items-center vai-border-b vai-px-3"
    // eslint-disable-next-line
    cmdk-input-wrapper=""
  >
    <Search className="vai-mr-2 vai-h-4 vai-w-4 vai-shrink-0 vai-opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'vai-flex vai-h-11 vai-w-full vai-rounded-md vai-bg-transparent vai-py-3 vai-text-sm vai-outline-none placeholder:vai-text-muted-foreground disabled:vai-cursor-not-allowed disabled:vai-opacity-50',
        className,
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(
      'vai-max-h-[300px] vai-overflow-y-auto vai-overflow-x-hidden',
      className,
    )}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="vai-py-6 vai-text-center vai-text-sm"
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'vai-overflow-hidden vai-p-1 vai-text-foreground [&_[cmdk-group-heading]]:vai-px-2 [&_[cmdk-group-heading]]:vai-py-1.5 [&_[cmdk-group-heading]]:vai-text-xs [&_[cmdk-group-heading]]:vai-font-medium [&_[cmdk-group-heading]]:vai-text-muted-foreground',
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('vai--mx-1 vai-h-px vai-bg-border', className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'vai-relative vai-flex vai-cursor-default vai-gap-2 vai-select-none vai-items-center vai-rounded-sm vai-px-2 vai-py-1.5 vai-text-sm vai-outline-none data-[disabled=true]:vai-pointer-events-none data-[selected=true]:vai-bg-accent data-[selected=true]:vai-text-accent-foreground data-[disabled=true]:vai-opacity-50 [&_svg]:vai-pointer-events-none [&_svg]:vai-size-4 [&_svg]:vai-shrink-0',
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'vai-ml-auto vai-text-xs vai-tracking-widest vai-text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
